import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { onNavDrawerOpen } from '@/renderer/utils/navDrawer';
import { getSafeAreaInsets } from '@/renderer/utils/safeAreaInsets';

type Point = { x: number; y: number };

// Touch-only guard against the native iOS text selection / copy-paste callout.
// Both the backdrop and the card need it: the backdrop mounts under the finger
// that is STILL held down from the long-press that opened the menu, and WebKit's
// own touch-and-hold selection gesture fires a beat after ours. If that gesture
// re-hit-tests onto an unguarded, selectable backdrop it selects the nearest
// selectable block — the whole page (#2021). The card is portaled to <body>,
// outside the pressed element's own guard, so it carries it too (#2006).
const TOUCH_SELECT_GUARD = 'select-none [-webkit-touch-callout:none] [-webkit-user-select:none]';

/**
 * A small floating menu anchored at a long-press point (see `useLongPress`),
 * rendered in a portal so it escapes the chat's `overflow` clipping. It sits
 * just above the finger (flipping below when there isn't room) and closes on an
 * outside tap, a scroll, a resize, Escape, or the mobile sidebar drawer opening.
 * Desktop keeps its hover affordances and never mounts this — it's the touch
 * counterpart.
 */
export function LongPressMenu({
  point,
  onClose,
  children,
}: {
  point: Point;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // Ignore the trailing tap of the press that opened the menu, so lifting the
  // finger doesn't immediately dismiss it.
  const [armed, setArmed] = useState(false);

  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    // Keep the card inside the safe area as well as the viewport: the native
    // shell paints edge-to-edge, so the viewport includes the notch / status bar
    // and home-indicator bands (insets are 0 on web).
    const inset = getSafeAreaInsets();
    const margin = 8;
    const minLeft = inset.left + margin;
    const maxLeft = window.innerWidth - inset.right - rect.width - margin;
    const minTop = inset.top + margin;
    const maxTop = window.innerHeight - inset.bottom - rect.height - margin;
    let left = point.x - rect.width / 2;
    let top = point.y - rect.height - 12;
    left = Math.max(minLeft, Math.min(left, maxLeft));
    if (top < minTop) {
      // No room above the finger — drop below it instead.
      top = point.y + 16;
    }
    top = Math.max(minTop, Math.min(top, maxTop));
    setPos({ left, top });
  }, [point.x, point.y]);

  useEffect(() => {
    const armTimer = setTimeout(() => setArmed(true), 350);
    const close = () => onClose();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    // The sidebar drawer is the top-most surface; never stay layered over it.
    const offNav = onNavDrawerOpen(close);
    return () => {
      clearTimeout(armTimer);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
      offNav();
    };
  }, [onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  const dismiss = () => {
    if (armed) {
      onClose();
    }
  };

  // Touch dismissal happens on touchEND, with the synthesized click suppressed.
  // Dismissing on touchstart unmounted the backdrop mid-gesture, so the tap's
  // click then landed on whatever was underneath it — a link navigated, the
  // compose button opened (#2021). preventDefault on touchend is what cancels
  // that click (touchstart listeners are passive in React, so it can't be done
  // there). Mouse/desktop still dismisses via the plain click.
  const dismissTouch = (event: React.TouchEvent) => {
    if (event.cancelable) {
      event.preventDefault();
    }
    dismiss();
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-50 ${TOUCH_SELECT_GUARD}`}
      onClick={dismiss}
      onTouchEnd={dismissTouch}
    >
      <div
        ref={cardRef}
        // `items-stretch` (not `items-center`) so the hosted action row controls
        // its own height and its segmented `divide-x` separators stay full-height
        // and aligned (#2019).
        className={`fixed flex w-max max-w-[calc(100dvw-1rem)] items-stretch overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-zinc-900/10 dark:bg-zinc-900 dark:ring-white/10 ${TOUCH_SELECT_GUARD}`}
        style={{
          left: pos?.left ?? point.x,
          top: pos?.top ?? point.y,
          visibility: pos ? 'visible' : 'hidden',
        }}
        onClick={(event) => event.stopPropagation()}
        onTouchEnd={(event) => event.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
